"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React from "react"

import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"
import { MexicanStates as MexicanStatesConst } from "@modules/signup-and-order-flow/mexican-states"

import DateInput from "../../common/DateInput"
import SelectInput from "../../common/SelectInput"
import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"
import { PERSONAL_INFO_STEP_TEXTS } from "../constants"

const MexicanStates: string[] = [...MexicanStatesConst]

const PersonalInfoStep: React.FC<StepComponentProps> = ({ onComplete, onBack, progressPercentage, stepId }) => {
  const personalInfoText = PERSONAL_INFO_STEP_TEXTS

  const { curpDetails, generateCurp } = useSignup()
  const { firstName, paternalLastName, maternalLastName, gender, dayOfBirth, monthOfBirth, yearOfBirth, stateOfBirth, dateOfBirth } =
    curpDetails

  const isFormValid =
    firstName.isValid &&
    paternalLastName.isValid &&
    maternalLastName.isValid &&
    gender.isValid &&
    dayOfBirth.isValid &&
    monthOfBirth.isValid &&
    yearOfBirth.isValid &&
    stateOfBirth.isValid

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    firstName.setTouched(true)
    firstName.setBlurred(true)
    paternalLastName.setTouched(true)
    paternalLastName.setBlurred(true)
    maternalLastName.setTouched(true)
    maternalLastName.setBlurred(true)
    gender.setTouched(true)
    gender.setBlurred(true)
    dayOfBirth.setTouched(true)
    dayOfBirth.setBlurred(true)
    monthOfBirth.setTouched(true)
    monthOfBirth.setBlurred(true)
    yearOfBirth.setTouched(true)
    yearOfBirth.setBlurred(true)
    dateOfBirth.setTouched(true)
    dateOfBirth.setBlurred(true)
    stateOfBirth.setTouched(true)
    stateOfBirth.setBlurred(true)

    if (!isFormValid) {
      return
    }

    try {
      await generateCurp.execute({
        firstName: firstName.value,
        paternalLastName: paternalLastName.value,
        maternalLastName: maternalLastName.value,
        gender: gender.value as "M" | "F",
        dayOfBirth: dayOfBirth.value,
        monthOfBirth: monthOfBirth.value,
        yearOfBirth: yearOfBirth.value,
        stateOfBirth: stateOfBirth.value,
      })

      onComplete()
    } catch (error) {
      console.error("Error generating CURP:", error)
      datadogRum.addError(error, {
        step: stepId,
        action: "generateCurp",
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const mapOptions = (options: string[]) => options.map((option) => ({ value: option, label: option }))

  const customTitle = personalInfoText.title

  return (
    <StepWrapper
      title={customTitle}
      subtitle={personalInfoText.subtitle}
      progressPercentage={progressPercentage}
      isLoading={generateCurp.isLoading}
      formError={generateCurp.error?.message}
      onSubmit={handleSubmit}
      submitDisabled={!isFormValid || generateCurp.isLoading}
      showBackButton={true}
      onBack={onBack}
    >
      <div>
        <TextInput
          label={personalInfoText.labels.firstName}
          value={firstName.value}
          onChange={(e) => firstName.setValue(e.target.value)}
          onBlur={() => {
            firstName.setTouched(true)
            firstName.setBlurred(true)
            if (firstName.errorMessage) {
              datadogRum.addError(new Error(`First name validation error: ${firstName.errorMessage}`), {
                step: stepId,
                field: "firstName",
              })
            }
          }}
          onFocus={() => firstName.setTouched(true)}
          isValid={firstName.isValid}
          touched={firstName.touched}
          blurred={firstName.blurred}
          errorMessage={firstName.errorMessage}
        />

        <TextInput
          label={personalInfoText.labels.paternalLastName}
          value={paternalLastName.value}
          onChange={(e) => paternalLastName.setValue(e.target.value)}
          onBlur={() => {
            paternalLastName.setTouched(true)
            paternalLastName.setBlurred(true)
            if (paternalLastName.errorMessage) {
              datadogRum.addError(new Error(`Paternal last name validation error: ${paternalLastName.errorMessage}`), {
                step: stepId,
                field: "paternalLastName",
              })
            }
          }}
          onFocus={() => paternalLastName.setTouched(true)}
          isValid={paternalLastName.isValid}
          touched={paternalLastName.touched}
          blurred={paternalLastName.blurred}
          errorMessage={paternalLastName.errorMessage}
        />

        <TextInput
          label={personalInfoText.labels.maternalLastName}
          value={maternalLastName.value}
          onChange={(e) => maternalLastName.setValue(e.target.value)}
          onBlur={() => {
            maternalLastName.setTouched(true)
            maternalLastName.setBlurred(true)
          }}
          onFocus={() => maternalLastName.setTouched(true)}
          isValid={maternalLastName.isValid}
          touched={maternalLastName.touched}
          blurred={maternalLastName.blurred}
          errorMessage={maternalLastName.errorMessage}
        />

        <DateInput label={personalInfoText.labels.dateOfBirth} dateOfBirth={dateOfBirth} />

        <SelectInput
          label={personalInfoText.labels.gender}
          options={[
            { value: "M", label: personalInfoText.options.male },
            { value: "F", label: personalInfoText.options.female },
          ]}
          value={gender.value}
          onChange={(e) => gender.setValue(e.target.value)}
          onBlur={() => {
            gender.setTouched(true)
            gender.setBlurred(true)
          }}
          onFocus={() => gender.setTouched(true)}
          isValid={gender.isValid}
          touched={gender.touched}
          blurred={gender.blurred}
          errorMessage={gender.errorMessage}
        />

        <SelectInput
          label={personalInfoText.labels.stateOfBirth}
          options={mapOptions(MexicanStates)}
          value={stateOfBirth.value}
          onChange={(e) => stateOfBirth.setValue(e.target.value)}
          onBlur={() => {
            stateOfBirth.setTouched(true)
            stateOfBirth.setBlurred(true)
          }}
          onFocus={() => stateOfBirth.setTouched(true)}
          isValid={stateOfBirth.isValid}
          touched={stateOfBirth.touched}
          blurred={stateOfBirth.blurred}
          errorMessage={stateOfBirth.errorMessage}
        />
      </div>
    </StepWrapper>
  )
}

export default PersonalInfoStep
